/**
 * Camera Component
 * Uses drei's CameraControls for smooth animations and camera positioning
 */

import React, { useRef, useEffect, useLayoutEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { CameraControls, PerspectiveCamera, CameraControlsImpl } from '@react-three/drei'
import { Vector3, Quaternion, Euler } from 'three'
import { getDeviceInfo } from '../../utils/deviceDetection'

const { ACTION } = CameraControlsImpl

// Module-level flag: true while a camera transition is in progress.
// Exported so PostProcessing.jsx and SunRotation can read the live
// camera transition state without prop-threading through app.jsx.
// `duration` is written at transition start so SunRotation can match it.
export const cameraTransitionState = { active: false, duration: 1.0, elapsed: 0 }

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

// Transition duration from the ON-SCREEN sweep of the move, not the raw world
// angle. A narrow (telephoto) fov magnifies motion, so the same world angle
// covers far more screen — the old formula (angle vs a fixed 60° reference)
// was calibrated for the fov-35 home views and read 3–4× too fast from the
// fov-10 desktop home. angle / avgFov ≈ screen-heights the aim travels;
// REFERENCE_SCREENS is 60° expressed in fov-35 screen-heights, so wide-fov
// tiers keep their exact previous timing.
const REFERENCE_SCREENS = (Math.PI / 3) / (35 * Math.PI / 180) // ≈ 1.714
function transitionDuration(startDir, endDir, sfovDeg, efovDeg) {
  const angle = Math.acos(Math.max(-1, Math.min(1, startDir.dot(endDir))))
  const avgFovRad = ((sfovDeg + efovDeg) / 2) * (Math.PI / 180)
  const screens = avgFovRad > 0 ? angle / avgFovRad : 0
  const normalised = Math.min(screens / REFERENCE_SCREENS, 1.0)
  return 1.6 - normalised * 0.6
}

export function Camera({
  config = null,
  position = [0, 5, 5],
  fov = 50,
  near = 0.1,
  far = 2000,
  lookAt = [0, 0, 0],
  controlsConfig = {},
  makeDefault = true,
  mouseFollowIntensity = 0.33,
  disableMouseFollow = false,
  trajectoryEditorMode = false,
  mobilePortraitLaunchMode = false,
  followModeActive = false,
  snapToConfig = null,   // ref — .current is true when an orientation snap is needed
}) {
  const deviceInfo = getDeviceInfo()
  const effectiveMouseFollowIntensity = (deviceInfo.isMobile || disableMouseFollow) ? 0 : mouseFollowIntensity

  const cameraRef = useRef()
  const controlsRef = useRef()
  const { gl } = useThree()
  const initialSetupDone = useRef(false)
  // True when a config change fired before the first-frame initial placement.
  // Replayed once initialSetupDone flips so the transition still animates.
  const pendingConfigRef = useRef(false)
  const mousePosRef = useRef({ x: 0, y: 0 })
  const smoothMousePos = useRef({ x: 0, y: 0 })

  // Mouse-follow is stored as a quaternion offset applied on top of the base
  // camera orientation each frame. This way it never fights CameraControls —
  // CameraControls sets the base orientation and we post-multiply our offset.
  // During transitions the offset is blended back to identity so it fades out
  // gracefully rather than snapping to zero when the transition starts.
  const mouseOffsetQ = useRef(new Quaternion())   // current applied offset
  const mouseTargetQ = useRef(new Quaternion())   // target offset (from smooth mouse pos)
  const baseQ        = useRef(new Quaternion())   // camera quat before mouse offset
  const _euler       = useRef(new Euler())        // scratch Euler for building mouseTargetQ

  // Refs for pan boundary enforcement in Artemis mode
  const cameraPositionRef = useRef(new Vector3())
  const targetPositionRef = useRef(new Vector3())
  // Scratch vector for slerp direction computation in useFrame (avoids allocation)
  const _slerpDir = useRef(new Vector3())

  // Single transition object drives position, lookAt, and FOV together each frame.
  // null = no transition running.
  // When active, CameraControls spring is zeroed so we have full per-frame control.
  const transitionRef = useRef(null)

  // Desired CameraControls smoothTime — driven imperatively in useFrame so that
  // React re-renders (from deferredShadow, deferredHdri, isTransitioning state
  // changes mid-transition) cannot clobber the controls.smoothTime = 0 we set
  // at transition start by re-applying the JSX smoothTime prop.
  const desiredSmoothTimeRef = useRef(0.25)

  // Last confirmed lookAt target — fallback if getTarget() returns [0,0,0] on reinit
  const lastKnownTargetRef = useRef(new Vector3())

  // Track previous trajectoryEditorMode so we can detect transitions OUT of Artemis.
  // Plain ref — not synced by an effect, just written inside the config layout effect.
  const prevTrajectoryEditorModeRef = useRef(trajectoryEditorMode)

  const cameraPosition = config?.position || position
  const cameraLookAt   = config?.lookAt   || lookAt
  const cameraFov      = config?.fov      || fov

  const mergedControlsConfig = config ? {
    minDistance: config.minDistance,
    maxDistance: config.maxDistance,
    ...controlsConfig,
  } : controlsConfig

  // Runs the transition-setup logic for the current `config` / `trajectoryEditorMode`.
  // Called from the layout effect on a config/mode change, and replayed once from
  // useFrame when a config change fired before the first-frame initial placement
  // (pendingConfigRef). Defined in the component body so it closes over the same
  // props/refs the effect would.
  const runConfigTransition = () => {
    const controls = controlsRef.current

    // ── Orientation snap (e.g. portrait ↔ landscape rotation) ─────────────
    // Skip all transition logic — immediately place camera at new config position.
    const shouldSnap = snapToConfig?.current === true
    if (shouldSnap) {
      snapToConfig.current = false   // consume — only snap once
      transitionRef.current = null
      cameraTransitionState.active = false
      // Zero smoothTime so CameraControls doesn't spring to the new position
      desiredSmoothTimeRef.current = 0
      controls.smoothTime = 0
      controls.draggingSmoothTime = 0
      // Resolve snap position: mode configs carry an explicit position; focused-location
      // configs do not — for those, hold the camera's current position and only re-aim.
      const snapPos = new Vector3()
      if (config.position) {
        snapPos.set(config.position[0], config.position[1], config.position[2])
      } else {
        controls.getPosition(snapPos)   // keep current position, re-aim only
      }
      controls.setLookAt(
        snapPos.x, snapPos.y, snapPos.z,
        cameraLookAt[0], cameraLookAt[1], cameraLookAt[2],
        false
      )
      // Flush the CameraControls spring state immediately so it doesn't lerp
      controls.update(0)
      lastKnownTargetRef.current.set(cameraLookAt[0], cameraLookAt[1], cameraLookAt[2])
      if (cameraRef.current) {
        cameraRef.current.fov = cameraFov
        cameraRef.current.updateProjectionMatrix()
      }
      // Restore normal smoothTime after snap
      desiredSmoothTimeRef.current = config?.smoothTime || 0.25
      prevTrajectoryEditorModeRef.current = trajectoryEditorMode
      return
    }

    const wasArtemis = prevTrajectoryEditorModeRef.current
    const isArtemis  = trajectoryEditorMode

    // Normalize azimuth before any transition to prevent multi-spin unwinding
    if (controls.azimuthAngle !== undefined) {
      const TWO_PI = Math.PI * 2
      if (prevTrajectoryEditorModeRef.current && !trajectoryEditorMode) {
        // Leaving Artemis — normalize to shortest path toward MAIN target position
        const targetAzimuth = Math.atan2(cameraPosition[0], cameraPosition[2])
        let current = ((controls.azimuthAngle % TWO_PI) + TWO_PI) % TWO_PI
        const norm = ((targetAzimuth % TWO_PI) + TWO_PI) % TWO_PI
        if (current - norm > Math.PI) current -= TWO_PI
        if (norm - current > Math.PI) current += TWO_PI
        controls.azimuthAngle = current
      } else {
        // Entering Artemis or other transitions — simple normalise to [0, 2π]
        controls.azimuthAngle = ((controls.azimuthAngle % TWO_PI) + TWO_PI) % TWO_PI
      }
    }

    if (isArtemis || wasArtemis) {
      // ── Artemis mode (entering or leaving) ──────────────────────────────────
      // CameraControls spring drives position + lookAt with smoothTime.
      // FOV is handled by transitionRef (artemis:true skips the per-frame setLookAt).
      // The FOV transition uses a longer, independent duration so the zoom is
      // smooth even on wide FOV swings (e.g. 2→35) — the position spring
      // continues to settle at its own speed.
      const artemisSmooth = config?.smoothTime || 0.25
      desiredSmoothTimeRef.current = artemisSmooth
      controls.setLookAt(
        cameraPosition[0], cameraPosition[1], cameraPosition[2],
        cameraLookAt[0], cameraLookAt[1], cameraLookAt[2],
        true
      )
      cameraTransitionState.active = true
      cameraTransitionState.duration = artemisSmooth
      transitionRef.current = {
        elapsed: 0,
        duration: artemisSmooth * 2,
        sfov: cameraRef.current?.fov ?? cameraFov,
        efov: cameraFov,
        artemis: true,
      }
    } else if (config?.position) {
      // ── Location / navigation mode WITH an explicit position ───────────────
      // Most focused-location configs have no position (see the branch below —
      // position frozen, re-aim only). When a config DOES carry a position (e.g.
      // SLS/Orion `mobilePosition`/`position`, or a legacy `mobilePositionOffset`
      // resolved in app.jsx's applyPositionOffset), animate BOTH position and lookAt over the
      // same slow, angle-based duration as a normal location change (custom
      // per-frame interp, easeInOutCubic) — NOT the fast spring — so it matches
      // the other location transitions.
      const startPos    = new Vector3()
      const startTarget = new Vector3()
      controls.getPosition(startPos)
      controls.getTarget(startTarget)
      if (startTarget.lengthSq() < 1e-6 && lastKnownTargetRef.current.lengthSq() > 1e-6) {
        startTarget.copy(lastKnownTargetRef.current)
      }
      // Duration from the on-screen sweep (fov-normalised), same formula as the
      // no-position branch below.
      const camPos   = new Vector3(startPos.x, startPos.y, startPos.z)
      const startDir = new Vector3(startTarget.x, startTarget.y, startTarget.z).sub(camPos).normalize()
      const endDir   = new Vector3(cameraLookAt[0], cameraLookAt[1], cameraLookAt[2]).sub(camPos).normalize()
      const duration = transitionDuration(startDir, endDir, cameraRef.current?.fov ?? cameraFov, cameraFov)

      desiredSmoothTimeRef.current = 0
      cameraTransitionState.active = true
      cameraTransitionState.duration = duration
      cameraTransitionState.elapsed = 0
      transitionRef.current = {
        elapsed: 0,
        duration,
        restoreSmoothTime: config?.smoothTime || 0.25,
        // Position tween endpoints (start → the offset position).
        spx: startPos.x, spy: startPos.y, spz: startPos.z,
        epx: cameraPosition[0], epy: cameraPosition[1], epz: cameraPosition[2],
        // Look-at tween endpoints (world-space lerp is fine here since both the
        // camera and target move; simplest to lerp the target directly).
        stx: startTarget.x, sty: startTarget.y, stz: startTarget.z,
        etx: cameraLookAt[0], ety: cameraLookAt[1], etz: cameraLookAt[2],
        sfov: cameraRef.current?.fov ?? cameraFov,
        efov: cameraFov,
        artemis: false,
        posTween: true, // signals the per-frame loop to lerp position too
      }
    } else {
      // ── Location / navigation mode ─────────────────────────────────────────
      // Position is frozen at the camera's current actual position — location
      // configs have no position field so only lookAt + FOV change.
      const startPos    = new Vector3()
      const startTarget = new Vector3()
      controls.getPosition(startPos)
      controls.getTarget(startTarget)

      if (startTarget.lengthSq() < 1e-6 && lastKnownTargetRef.current.lengthSq() > 1e-6) {
        startTarget.copy(lastKnownTargetRef.current)
      }

      const camPos   = new Vector3(startPos.x, startPos.y, startPos.z)
      const startDir = new Vector3(startTarget.x, startTarget.y, startTarget.z).sub(camPos).normalize()
      const endVec   = new Vector3(cameraLookAt[0], cameraLookAt[1], cameraLookAt[2]).sub(camPos)
      const endDist  = endVec.length()
      const endDir   = endVec.clone().normalize()
      const duration = transitionDuration(startDir, endDir, cameraRef.current?.fov ?? cameraFov, cameraFov)

      desiredSmoothTimeRef.current = 0

      cameraTransitionState.active = true
      cameraTransitionState.duration = duration
      cameraTransitionState.elapsed = 0
      transitionRef.current = {
        elapsed: 0,
        duration,
        restoreSmoothTime: config?.smoothTime || 0.25,
        px: startPos.x, py: startPos.y, pz: startPos.z,
        sdx: startDir.x, sdy: startDir.y, sdz: startDir.z,
        edx: endDir.x,   edy: endDir.y,   edz: endDir.z,
        endDist,
        etx: cameraLookAt[0], ety: cameraLookAt[1], etz: cameraLookAt[2],
        sfov: cameraRef.current?.fov ?? cameraFov,
        efov: cameraFov,
        artemis: false,
      }
    }

    // Record current mode so next run can detect direction of change
    prevTrajectoryEditorModeRef.current = trajectoryEditorMode
  }

  // ── Transition setup on config or mode change ──────────────────────────────
  // Depends on BOTH config and trajectoryEditorMode so it always has the current
  // value of both when either changes — no separate ref-sync effect needed.
  // useLayoutEffect fires before the browser paints and before the next useFrame.
  useLayoutEffect(() => {
    if (!config || !initialSetupDone.current || !controlsRef.current) {
      if (cameraRef.current) {
        cameraRef.current.fov = cameraFov
        cameraRef.current.updateProjectionMatrix()
      }
      // If a config change arrived before the first-frame initial placement has
      // run, DON'T drop it — record it as pending. Once useFrame does the initial
      // placement and flips initialSetupDone, it replays runConfigTransition() so
      // the move still animates. Without this, an early click (before the first
      // frame) hit this early-return, lost the transition, and the camera snapped
      // instantly — the rare "instant instead of animated" first-load jump.
      if (config && !initialSetupDone.current) pendingConfigRef.current = true
      return
    }
    runConfigTransition()
  }, [config, trajectoryEditorMode])

  // ── Mouse tracking ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (effectiveMouseFollowIntensity === 0) return
    const canvas = gl.domElement
    if (!canvas) return
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect()
      mousePosRef.current.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1
      mousePosRef.current.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1
    }
    canvas.addEventListener('mousemove', handleMouseMove)
    return () => canvas.removeEventListener('mousemove', handleMouseMove)
  }, [gl, effectiveMouseFollowIntensity])

  // Expose controls ref to window for trajectory editor / mobile camera.
  // NOTE: window.orbitControlsRef is kept current in useFrame (below) rather
  // than a one-shot useEffect, because CameraControls recreates its internal
  // instance when mouseButtons/touches props change (e.g. followModeActive
  // toggle). A one-shot effect would cache the first instance and leave
  // MobilePortraitLaunchCamera with a stale dead reference.
  useEffect(() => () => { delete window.orbitControlsRef }, [])

  // Enable all render layers once on mount, except layer 4 (chase-cam-only objects)
  useEffect(() => {
    if (cameraRef.current) {
      cameraRef.current.layers.enableAll()
      cameraRef.current.layers.disable(4)
    }
  }, [])

  // ── Per-frame loop ─────────────────────────────────────────────────────────
  // Priority -2: must run BEFORE CameraControls' useFrame (priority -1).
  // Execution order each frame:
  //   -2  Camera.jsx: setLookAt(..., false) → snaps _target/_spherical in spring state
  //   -1  CameraControls: update(delta) → reads spring state → writes camera.position/quaternion
  //    0  R3F render → camera is exactly where we set it
  // Without this priority, Camera.jsx ran at 0 (after CameraControls), so setLookAt
  // would write spring state for frame N+1 while the camera rendered frame N's state —
  // producing the one-frame-behind jitter seen during lookAt transitions.
  useFrame((state, delta) => {
    // Keep window.orbitControlsRef current every frame — CameraControls can
    // recreate its instance when props like mouseButtons change, so a one-shot
    // useEffect would leave a stale reference after followModeActive toggles.
    if (controlsRef.current) window.orbitControlsRef = controlsRef.current

    // Initial placement — runs only on the first frame after mount. Kept in
    // useFrame (not the layout effect) because CameraControls needs its own mount
    // setup to have run first for setLookAt to stick — placing it at layout-commit
    // broke the initial home framing.
    if (!initialSetupDone.current && controlsRef.current) {
      controlsRef.current.setLookAt(
        cameraPosition[0], cameraPosition[1], cameraPosition[2],
        cameraLookAt[0],   cameraLookAt[1],   cameraLookAt[2],
        false
      )
      lastKnownTargetRef.current.set(cameraLookAt[0], cameraLookAt[1], cameraLookAt[2])
      if (cameraRef.current) {
        cameraRef.current.fov = cameraFov
        cameraRef.current.updateProjectionMatrix()
      }
      prevTrajectoryEditorModeRef.current = trajectoryEditorMode
      initialSetupDone.current = true

      // Replay a config change that arrived before this initial placement (e.g. an
      // early click on SLS/Orion or a mission launch). The layout effect recorded it
      // as pending instead of dropping it — now that setup is done, run the real
      // transition so the move animates instead of snapping.
      if (pendingConfigRef.current) {
        pendingConfigRef.current = false
        runConfigTransition()
      }
    }

    // ── Active transition ──────────────────────────────────────────────────
    const tr = transitionRef.current
    if (tr) {
      tr.elapsed = Math.min(tr.elapsed + delta, tr.duration)
      cameraTransitionState.elapsed = tr.elapsed
      const raw = tr.elapsed / tr.duration
      const t   = easeInOutCubic(raw)

      if (cameraRef.current) {
        // Geometric fov interpolation — magnification ≈ 1/fov, so a LINEAR fov
        // lerp front-loads the apparent zoom when the fovs differ a lot (e.g.
        // the fov-10 desktop home → fov-4 locations) and reads as a lurch.
        // sfov·(efov/sfov)^t keeps the perceived zoom rate constant.
        cameraRef.current.fov = tr.sfov * Math.pow(tr.efov / tr.sfov, t)
        cameraRef.current.updateProjectionMatrix()
      }

      if (tr.posTween && controlsRef.current) {
        // Offset move: lerp BOTH the camera position and the look-at target from
        // start to end over the (slow) duration, easeInOutCubic on t.
        controlsRef.current.setLookAt(
          tr.spx + (tr.epx - tr.spx) * t,
          tr.spy + (tr.epy - tr.spy) * t,
          tr.spz + (tr.epz - tr.spz) * t,
          tr.stx + (tr.etx - tr.stx) * t,
          tr.sty + (tr.ety - tr.sty) * t,
          tr.stz + (tr.etz - tr.stz) * t,
          false
        )
      } else if (!tr.artemis && controlsRef.current) {
        // Slerp between start and end look directions (unit vectors from camera).
        // This traces a straight line in screen/angular space — world-space lerp
        // curves on screen when targets are at different distances from the camera.
        const sd = _slerpDir.current
        sd.set(tr.sdx, tr.sdy, tr.sdz)
          .lerp({ x: tr.edx, y: tr.edy, z: tr.edz }, t)
          .normalize()
        controlsRef.current.setLookAt(
          tr.px, tr.py, tr.pz,
          tr.px + sd.x * tr.endDist,
          tr.py + sd.y * tr.endDist,
          tr.pz + sd.z * tr.endDist,
          false
        )
      }

      if (tr.elapsed >= tr.duration) {
        if (cameraRef.current) {
          cameraRef.current.fov = tr.efov
          cameraRef.current.updateProjectionMatrix()
        }
        if (!tr.artemis) {
          desiredSmoothTimeRef.current = tr.restoreSmoothTime
          lastKnownTargetRef.current.set(tr.etx, tr.ety, tr.etz)
        }
        transitionRef.current = null
        cameraTransitionState.active = false
      }
    }

    // ── Apply smoothTime imperatively every frame ──────────────────────────
    // This ensures React re-renders (deferred state changes mid-transition)
    // cannot clobber the smoothTime=0 we set at transition start via the JSX
    // smoothTime prop being re-applied during reconciliation.
    // When followModeActive is true, MobilePortraitLaunchCamera owns smoothTime.
    // When followCameraReturning is true, the return spring is in flight and
    // MobilePortraitLaunchCamera still owns smoothTime — do not override either.
    if (controlsRef.current && !followModeActive && !window.followCameraReturning) {
      controlsRef.current.smoothTime = desiredSmoothTimeRef.current
      controlsRef.current.draggingSmoothTime = desiredSmoothTimeRef.current
    }

    // ── Mouse-follow as quaternion offset ─────────────────────────────────
    // Always lerp smoothMousePos — toward cursor when active, toward zero when
    // inactive (so re-enabling doesn't snap).
    const smoothFactor = 1 - Math.pow(0.001, delta)
    const targetX = (effectiveMouseFollowIntensity > 0) ? mousePosRef.current.x : 0
    const targetY = (effectiveMouseFollowIntensity > 0) ? mousePosRef.current.y : 0
    smoothMousePos.current.x += (targetX - smoothMousePos.current.x) * smoothFactor
    smoothMousePos.current.y += (targetY - smoothMousePos.current.y) * smoothFactor

    if (!trajectoryEditorMode && cameraRef.current && initialSetupDone.current) {
      const intensity = effectiveMouseFollowIntensity * 0.1

      // Build target quaternion from current smooth mouse position.
      // During a transition, or when intensity is 0, target is identity — the
      // offset fades out smoothly instead of snapping off.
      if (effectiveMouseFollowIntensity > 0 && !transitionRef.current) {
        _euler.current.set(
          smoothMousePos.current.y * intensity,
          -smoothMousePos.current.x * intensity,
          0,
          'YXZ'
        )
        mouseTargetQ.current.setFromEuler(_euler.current)
      } else {
        mouseTargetQ.current.identity()
      }

      // Slerp the applied offset toward its target each frame.
      // Speed 8 = snappy but not instant; still smooths out jitter.
      const slerpAlpha = Math.min(delta * 8, 1)
      mouseOffsetQ.current.slerp(mouseTargetQ.current, slerpAlpha)

      // Save the base quaternion CameraControls has set, then apply offset on top.
      // This is non-destructive — next frame CameraControls will overwrite the
      // base again before we re-apply, so there is no drift accumulation.
      baseQ.current.copy(cameraRef.current.quaternion)
      cameraRef.current.quaternion.multiplyQuaternions(baseQ.current, mouseOffsetQ.current)
    }

    // ── Pan boundary enforcement (Artemis mode only) ───────────────────────
    if (trajectoryEditorMode && controlsRef.current && config?.panBoundary && config?.lookAt) {
      controlsRef.current.getPosition(cameraPositionRef.current)
      controlsRef.current.getTarget(targetPositionRef.current)
      const originalTarget = new Vector3(config.lookAt[0], config.lookAt[1], config.lookAt[2])
      const targetOffset   = targetPositionRef.current.clone().sub(originalTarget)
      if (targetOffset.length() > config.panBoundary) {
        targetOffset.normalize().multiplyScalar(config.panBoundary)
        const clampedTarget     = originalTarget.clone().add(targetOffset)
        const newCameraPosition = clampedTarget.clone().add(
          cameraPositionRef.current.clone().sub(targetPositionRef.current)
        )
        controlsRef.current.setLookAt(
          newCameraPosition.x, newCameraPosition.y, newCameraPosition.z,
          clampedTarget.x,     clampedTarget.y,     clampedTarget.z,
          true
        )
      }
    }

    // ── Azimuth normalisation (prevents spin accumulation) ─────────────────
    if ((trajectoryEditorMode || followModeActive) && controlsRef.current?.active === false) {
      const TWO_PI = Math.PI * 2
      controlsRef.current.azimuthAngle =
        ((controlsRef.current.azimuthAngle % TWO_PI) + TWO_PI) % TWO_PI
    }
  }, -2)

  const getTouchActions = () => {
    if (mobilePortraitLaunchMode && deviceInfo.isPhone) {
      return { one: ACTION.TOUCH_ROTATE, two: ACTION.NONE, three: ACTION.NONE }
    }
    if (!trajectoryEditorMode) {
      return { one: ACTION.NONE, two: ACTION.NONE, three: ACTION.NONE }
    }
    if (deviceInfo.isMobile && deviceInfo.isPortrait) {
      return { one: ACTION.TOUCH_ROTATE, two: ACTION.NONE, three: ACTION.NONE }
    }
    if (deviceInfo.isTablet) {
      return { one: ACTION.TOUCH_ROTATE, two: ACTION.TOUCH_DOLLY_TRUCK, three: ACTION.NONE }
    }
    return { one: ACTION.TOUCH_ROTATE, two: ACTION.NONE, three: ACTION.NONE }
  }

  return (
    <>
      <PerspectiveCamera
        ref={cameraRef}
        makeDefault={makeDefault}
        near={near}
        far={far}
        /* fov set imperatively in useFrame — never passed as a prop */
      />

      {/* No camera prop — CameraControls defaults to R3F's state.camera, which is the
          makeDefault PerspectiveCamera above. Omitting it prevents the useMemo inside
          CameraControls from ever recreating the instance (which resets _target to [0,0,0]). */}
      <CameraControls
        ref={controlsRef}
        minDistance={mergedControlsConfig.minDistance || 1}
        maxDistance={mergedControlsConfig.maxDistance || 20}
        /* smoothTime and draggingSmoothTime are driven imperatively in useFrame
           via desiredSmoothTimeRef — never as JSX props — so that React re-renders
           mid-transition cannot clobber the smoothTime=0 set at transition start. */
        mouseButtons={followModeActive ? {
          left: ACTION.ROTATE,
          middle: ACTION.NONE,
          right: ACTION.NONE,
          wheel: ACTION.NONE,
        } : (trajectoryEditorMode && !(deviceInfo.isMobile && deviceInfo.isPortrait)) ? {
          left: ACTION.ROTATE,
          middle: ACTION.NONE,
          right: ACTION.TRUCK,
          wheel: ACTION.DOLLY,
        } : trajectoryEditorMode ? {
          left: ACTION.ROTATE,
          middle: ACTION.NONE,
          right: ACTION.NONE,
          wheel: ACTION.NONE,
        } : {
          left: ACTION.NONE,
          middle: ACTION.NONE,
          right: ACTION.NONE,
          wheel: ACTION.NONE,
        }}
        touches={followModeActive
          ? { one: ACTION.TOUCH_ROTATE, two: ACTION.NONE, three: ACTION.NONE }
          : getTouchActions()
        }
        {...mergedControlsConfig}
      />
    </>
  )
}

export default Camera
